(() => {
  const cleanText = (value, fallback = '') => String(value || '').replace(/\s+/g, ' ').trim() || fallback;
  const localizedCopyFor = (root) => {
    let dictionary = {};
    try {
      const parsed = JSON.parse(root.querySelector('[data-tan-quick-quote-copy]')?.textContent || '{}');
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) dictionary = parsed;
    } catch (_) {
      dictionary = {};
    }
    return (key, replacements = {}) => {
      let output = typeof dictionary[key] === 'string' ? dictionary[key] : '';
      Object.entries(replacements).forEach(([name, value]) => {
        const tokenName = String(name).replace(/[^a-z0-9]+/gi, '_').toUpperCase();
        output = output.split(`__TAN_${tokenName}__`).join(String(value ?? ''));
        output = output.replace(new RegExp(`\\{\\{\\s*${name}\\s*\\}\\}`, 'g'), String(value ?? ''));
      });
      return output;
    };
  };
  const submissionStoragePrefix = 'tan-quote-submission:v1';
  const submissionMaximumAge = 2 * 60 * 60 * 1000;
  const quoteSlug = (value, fallback = 'general') => {
    let normalized = cleanText(value).toLowerCase();
    try { normalized = normalized.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); } catch (_) { /* Keep the original text. */ }
    normalized = normalized
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return normalized || fallback;
  };
  const normalizeBudgetBand = (value) => {
    const normalized = cleanText(value).toLowerCase();
    return ['under-300', '300-plus'].includes(normalized) ? normalized : 'unknown';
  };
  const budgetBandFor = (baseEur, selected, noBudget) => {
    const value = Number(baseEur);
    if (!selected || noBudget || !Number.isFinite(value)) return 'unknown';
    return value >= 300 ? '300-plus' : 'under-300';
  };
  const submissionStorageKey = (root) => `${submissionStoragePrefix}:${window.location.pathname}:${cleanText(root?.dataset?.marketCountry, 'unknown').toUpperCase()}`;
  const reportedSubmissionKey = (root) => `tan-quote-reported:v1:${window.location.pathname}:${cleanText(root?.dataset?.marketCountry, 'unknown').toUpperCase()}`;
  const readReportedSubmissionId = (root) => {
    try { return cleanText(window.sessionStorage.getItem(reportedSubmissionKey(root))); } catch (_) { return ''; }
  };
  const markReportedSubmission = (root, submissionId) => {
    try { window.sessionStorage.setItem(reportedSubmissionKey(root), cleanText(submissionId)); } catch (_) { /* In-memory deduplication still applies. */ }
  };
  const removePendingSubmission = (root) => {
    try { window.sessionStorage.removeItem(submissionStorageKey(root)); } catch (_) { /* Success still remains visible. */ }
  };
  const readPendingSubmission = (root) => {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(submissionStorageKey(root)) || 'null');
      const age = Date.now() - Number(stored?.createdAt || 0);
      if (!stored || stored.version !== 1 || stored.pathname !== window.location.pathname || age < 0 || age > submissionMaximumAge) {
        removePendingSubmission(root);
        return null;
      }
      return stored;
    } catch (_) {
      removePendingSubmission(root);
      return null;
    }
  };
  const successUrlFor = (record, href = window.location.href) => {
    const url = new URL(href, window.location.origin);
    const existingCategory = quoteSlug(url.searchParams.get('quote_category'));
    const existingType = quoteSlug(url.searchParams.get('quote_type'));
    const existingPlacement = quoteSlug(url.searchParams.get('quote_placement'), '');
    const existingBudgetBand = normalizeBudgetBand(url.searchParams.get('quote_budget'));
    const placement = record
      ? quoteSlug(record.placement, '')
      : existingPlacement;
    const budgetBand = record
      ? normalizeBudgetBand(record.budgetBand)
      : existingBudgetBand;

    url.searchParams.set('quote_submitted', '1');
    url.searchParams.set('quote_form', 'logo_quote');
    url.searchParams.set('quote_category', quoteSlug(record?.category, existingCategory));
    url.searchParams.set('quote_type', quoteSlug(record?.type, existingType));
    if (placement) url.searchParams.set('quote_placement', placement);
    else url.searchParams.delete('quote_placement');
    url.searchParams.set('quote_budget', budgetBand);
    url.hash = '';
    return url;
  };
  const marketExamples = {
    AT: { city: 'Vienna', phone: '+43 660 0000000' },
    AU: { city: 'Sydney', phone: '+61 400 000 000' },
    BE: { city: 'Brussels', phone: '+32 470 00 00 00' },
    BG: { city: 'Sofia', phone: '+359 88 000 0000' },
    CA: { city: 'Toronto', phone: '+1 416 555 0100' },
    CH: { city: 'Zurich', phone: '+41 79 000 00 00' },
    CY: { city: 'Nicosia', phone: '+357 96 000000' },
    CZ: { city: 'Prague', phone: '+420 601 000 000' },
    DE: { city: 'Berlin', phone: '+49 1512 0000000' },
    DK: { city: 'Copenhagen', phone: '+45 20 00 00 00' },
    EE: { city: 'Tallinn', phone: '+372 500 0000' },
    ES: { city: 'Madrid', phone: '+34 600 00 00 00' },
    FI: { city: 'Helsinki', phone: '+358 40 000 0000' },
    FR: { city: 'Paris', phone: '+33 6 00 00 00 00' },
    GB: { city: 'London', phone: '+44 7700 900000' },
    GR: { city: 'Athens', phone: '+30 690 000 0000' },
    HR: { city: 'Zagreb', phone: '+385 91 000 0000' },
    HU: { city: 'Budapest', phone: '+36 20 000 0000' },
    IE: { city: 'Dublin', phone: '+353 85 000 0000' },
    IS: { city: 'Reykjavik', phone: '+354 600 0000' },
    IT: { city: 'Milan', phone: '+39 320 000 0000' },
    JP: { city: 'Tokyo', phone: '+81 90 0000 0000' },
    LT: { city: 'Vilnius', phone: '+370 600 00000' },
    LU: { city: 'Luxembourg', phone: '+352 621 000 000' },
    LV: { city: 'Riga', phone: '+371 20 000 000' },
    MT: { city: 'Valletta', phone: '+356 9900 0000' },
    NL: { city: 'Amsterdam', phone: '+31 6 00000000' },
    NO: { city: 'Oslo', phone: '+47 400 00 000' },
    NZ: { city: 'Auckland', phone: '+64 21 000 0000' },
    PL: { city: 'Warsaw', phone: '+48 500 000 000' },
    PT: { city: 'Lisbon', phone: '+351 910 000 000' },
    RO: { city: 'Bucharest', phone: '+40 720 000 000' },
    SE: { city: 'Stockholm', phone: '+46 70 000 00 00' },
    SG: { city: 'Singapore', phone: '+65 8000 0000' },
    SI: { city: 'Ljubljana', phone: '+386 40 000 000' },
    SK: { city: 'Bratislava', phone: '+421 900 000 000' },
    TR: { city: 'Istanbul', phone: '+90 532 000 00 00' },
    US: { city: 'New York', phone: '+1 212 555 0100' },
  };
  const emailDomainCorrections = {
    'gamil.com': 'gmail.com',
    'gmai.com': 'gmail.com',
    'gmail.cmo': 'gmail.com',
    'gmail.con': 'gmail.com',
    'gmial.com': 'gmail.com',
    'hotmai.com': 'hotmail.com',
    'hotmail.con': 'hotmail.com',
    'hotnail.com': 'hotmail.com',
    'icloud.con': 'icloud.com',
    'outllook.com': 'outlook.com',
    'outlok.com': 'outlook.com',
    'outlook.con': 'outlook.com',
    'yaho.com': 'yahoo.com',
    'yahoo.con': 'yahoo.com',
  };

  function init(root) {
    if (!root || root.dataset.quickQuoteReady === 'true') return;
    root.dataset.quickQuoteReady = 'true';

    const form = root.querySelector('form');
    if (!form) return;
    const copy = localizedCopyFor(root);
    const successPanel = root.querySelector('[data-quick-success]');

    const steps = Array.from(root.querySelectorAll('[data-quick-step]'));
    const errorBox = root.querySelector('[data-quick-error]');
    const submitButton = root.querySelector('[data-quick-submit]');
    const submitLabel = root.querySelector('[data-quick-submit-label]');
    const submitLabelDefault = cleanText(submitLabel?.textContent, copy('submit_default'));
    const restoreBanner = root.querySelector('[data-quote-restore-banner]');
    const clearDraftButton = root.querySelector('[data-quote-clear-draft]');
    const budgetRange = root.querySelector('[data-budget-range]');
    const budgetOutput = root.querySelector('[data-budget-output]');
    const budgetField = root.querySelector('[data-budget-field]');
    const budgetBaseField = root.querySelector('[data-budget-base-field]');
    const budgetCurrencyField = root.querySelector('[data-budget-currency-field]');
    const budgetRateField = root.querySelector('[data-budget-rate-field]');
    const budgetTierField = root.querySelector('[data-budget-tier-field]');
    const quoteEmailSummaryField = root.querySelector('[data-quote-email-summary]');
    const currencySelect = root.querySelector('[data-budget-currency-select]');
    const budgetUnset = root.querySelector('[data-budget-unset]');
    const budgetShowcase = root.querySelector('[data-budget-showcase]');
    const budgetCards = Array.from(root.querySelectorAll('[data-budget-card]'));
    const budgetDetails = Array.from(root.querySelectorAll('[data-budget-detail]'));
    const budgetUnsure = root.querySelector('[data-budget-unsure]');
    const budgetTicks = Array.from(root.querySelectorAll('[data-budget-tick]'));
    const purposeChoices = Array.from(root.querySelectorAll('[data-purpose-choice]'));
    const purposePanels = Array.from(root.querySelectorAll('[data-purpose-panel]'));
    const deliveryCountry = root.querySelector('[data-delivery-country]');
    const deliveryCity = root.querySelector('[data-delivery-city]');
    const deliveryPostcode = root.querySelector('[data-delivery-postcode]');
    const postcodeUnsure = root.querySelector('[data-postcode-unsure]');
    const contactNameInput = root.querySelector('input[name="contact[name]"]');
    const emailInput = root.querySelector('input[name="contact[email]"]');
    const reviewName = root.querySelector('[data-review-name]');
    const reviewEmail = root.querySelector('[data-review-email]');
    const reviewProject = root.querySelector('[data-review-project]');
    const reviewSetup = root.querySelector('[data-review-setup]');
    const reviewSizeDelivery = root.querySelector('[data-review-size-delivery]');
    const reviewBudget = root.querySelector('[data-review-budget]');
    const reviewNotes = root.querySelector('[data-review-notes]');
    const reviewPrimaryFiles = root.querySelector('[data-review-primary-files]');
    const reviewSecondaryFiles = root.querySelector('[data-review-secondary-files]');
    const reviewNotesSection = root.querySelector('[data-review-section="notes"]');
    const reviewPrimaryFilesSection = root.querySelector('[data-review-section="primary-files"]');
    const reviewSecondaryFilesSection = root.querySelector('[data-review-section="secondary-files"]');
    const reviewEmptyFilesSection = root.querySelector('[data-review-section="files-empty"]');
    const finalFilesSection = root.querySelector('[data-final-files-section]');
    const uploadedFileLinksField = root.querySelector('[data-upload-file-links]');
    const emailSuggestion = root.querySelector('[data-email-suggestion]');
    const emailCorrectionButton = root.querySelector('[data-email-correction]');
    const reviewEditButtons = Array.from(root.querySelectorAll('[data-review-edit], [data-review-edit-target]'));
    const marketCountry = cleanText(root.dataset.marketCountry, 'unknown').toUpperCase();
    const marketCurrency = cleanText(root.dataset.currency, 'EUR').toUpperCase();
    const baseCurrency = cleanText(root.dataset.baseCurrency, 'EUR').toUpperCase();
    const locale = cleanText(root.dataset.locale, document.documentElement.lang || 'en');
    const trackingEnabled = cleanText(root.dataset.quoteTracking, 'true') !== 'false';
    const draftDays = Math.max(1, Number(root.dataset.draftDays) || 14);
    const currencyStorageKey = `tan-quote-currency:${marketCountry || 'default'}`;
    const draftStorageKey = `tan-quote-draft:v3:${window.location.pathname}:${marketCountry || 'default'}`;
    const visitedSteps = new Set();
    const completedSteps = new Set();
    let currentStep = 1;
    let saveTimer = null;
    let stepTransitioning = false;
    let budgetSelected = true;
    let quoteStarted = false;
    let lastTrackedUploadCount = 0;
    let anonymousUploadSequence = 0;

    const removeDraft = () => {
      if (saveTimer) window.clearTimeout(saveTimer);
      try { window.localStorage.removeItem(draftStorageKey); } catch (_) { /* Storage can be unavailable. */ }
    };

    const setSubmittingState = (submitting) => {
      if (submitButton) {
        submitButton.disabled = submitting;
        if (submitting) submitButton.setAttribute('aria-busy', 'true');
        else submitButton.removeAttribute('aria-busy');
      }
      if (submitLabel) submitLabel.textContent = submitting ? copy('sending_request') : submitLabelDefault;
    };

    root.querySelectorAll('[data-upload-app-slot]').forEach((slot) => {
      const placeholder = slot.querySelector('[data-upload-lift-placeholder]');
      if (!placeholder) return;
      const syncPlaceholder = () => {
        const hasUploadWidget = Array.from(slot.children).some((child) => child !== placeholder);
        placeholder.hidden = hasUploadWidget;
      };
      new MutationObserver(syncPlaceholder).observe(slot, { childList: true });
      slot.addEventListener('upload:ready', () => { placeholder.hidden = true; });
      syncPlaceholder();
    });

    const fallbackCurrencyConfig = {
      baseCurrency,
      asOf: 'not supplied',
      source: 'Theme fallback',
      currencies: {
        [baseCurrency]: { rate: 1, roundTo: 10 },
        [marketCurrency]: { rate: 1, roundTo: 10 },
      },
    };
    const currencyConfig = window.TAN_QUOTE_CURRENCY_CONFIG || fallbackCurrencyConfig;
    const currencyDetailsFor = (currencyCode) => currencyConfig.currencies?.[currencyCode] || null;
    const isSupportedCurrency = (currencyCode) => Boolean(currencyDetailsFor(currencyCode));
    let selectedCurrency = isSupportedCurrency(marketCurrency) ? marketCurrency : baseCurrency;

    if (currencySelect) {
      Array.from(currencySelect.options).forEach((option) => {
        option.value = cleanText(option.value).toUpperCase();
        if (!isSupportedCurrency(option.value)) option.remove();
      });

      if (!Array.from(currencySelect.options).some((option) => option.value === selectedCurrency)) {
        const option = document.createElement('option');
        option.value = selectedCurrency;
        option.textContent = selectedCurrency;
        currencySelect.prepend(option);
      }

      try {
        const storedCurrency = cleanText(window.sessionStorage.getItem(currencyStorageKey)).toUpperCase();
        if (isSupportedCurrency(storedCurrency) && Array.from(currencySelect.options).some((option) => option.value === storedCurrency)) {
          selectedCurrency = storedCurrency;
        }
      } catch (_) {
        // Market currency remains the default when storage is unavailable.
      }
      currencySelect.value = selectedCurrency;
    }

    const setError = (message = '') => {
      if (!errorBox) return;
      errorBox.textContent = message;
      errorBox.hidden = !message;
    };

    const stepFor = (stepNumber) => steps.find((item) => Number(item.dataset.quickStep) === Number(stepNumber));
    const stepNameFor = (stepNumber) => cleanText(stepFor(stepNumber)?.dataset.stepName, `Step ${stepNumber}`);

    const currentBudgetBand = () => budgetBandFor(
      Number(budgetRange?.value),
      budgetSelected,
      Boolean(budgetUnset?.checked),
    );
    const currentBudgetTierCode = () => {
      if (budgetUnset?.checked) return 'unsure';
      const level = cleanText(root.dataset.budgetLevel);
      return /^\d+$/.test(level) ? `level_${level}` : 'not_selected';
    };

    const trackingContext = (extra = {}) => ({
      funnel_name: 'custom_neon_quote',
      market_country: marketCountry,
      quote_currency: selectedCurrency,
      sign_purpose: cleanText(root.dataset.signPurpose, 'not_selected'),
      budget_tier: currentBudgetTierCode(),
      budget_band: currentBudgetBand(),
      restored_draft: root.dataset.draftRestored === 'true',
      ...extra,
    });

    const emitTracking = (eventName, extra = {}) => {
      if (!trackingEnabled) return;
      const parameters = trackingContext(extra);
      root.dispatchEvent(
        new CustomEvent('tan:quote:event', {
          bubbles: true,
          detail: { event: eventName, ...parameters },
        })
      );
    };

    const trackQuoteStartOnce = () => {
      if (quoteStarted) return;
      quoteStarted = true;
      emitTracking('quote_start', { step_number: currentStep, step_name: stepNameFor(currentStep) });
    };

    if (successPanel) {
      const submission = readPendingSubmission(root);
      const successUrl = successUrlFor(submission);
      try { window.history.replaceState(window.history.state, '', `${successUrl.pathname}${successUrl.search}`); } catch (_) { /* The confirmed success remains valid. */ }
      if (submission) {
        const submissionId = cleanText(submission.submissionId, String(submission.createdAt || ''));
        const alreadyReported = submissionId && readReportedSubmissionId(root) === submissionId;
        const shouldTrack = !alreadyReported && root.dataset.quoteSuccessTracked !== 'true';
        root.dataset.quoteSuccessTracked = 'true';
        if (shouldTrack) {
          emitTracking('generate_lead', {
            step_number: 6,
            step_name: 'Contact & submit',
            market_country: cleanText(submission.marketCountry, marketCountry).toUpperCase(),
            quote_currency: cleanText(submission.quoteCurrency, selectedCurrency).toUpperCase(),
            sign_purpose: cleanText(submission.category, 'general'),
            quote_category: cleanText(submission.category, 'general'),
            quote_type: cleanText(submission.type, 'general'),
            quote_placement: cleanText(submission.placement),
            budget_tier: cleanText(submission.budgetTier, 'not_selected'),
            budget_band: normalizeBudgetBand(submission.budgetBand),
            has_file: Boolean(submission.hasFile),
            file_count: Math.max(0, Math.min(8, Math.floor(Number(submission.fileCount) || 0))),
            restored_draft: Boolean(submission.restoredDraft),
            success_path: `${successUrl.pathname}${successUrl.search}`,
          });
          if (submissionId) markReportedSubmission(root, submissionId);
        }
      }
      removePendingSubmission(root);
      removeDraft();
      const revealSuccess = () => {
        successPanel.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'nearest' });
        successPanel.focus({ preventScroll: true });
      };
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => window.requestAnimationFrame(revealSuccess));
      } else {
        revealSuccess();
      }
      return;
    }

    const trackStepView = (stepNumber) => {
      if (visitedSteps.has(stepNumber)) return;
      visitedSteps.add(stepNumber);
      emitTracking('quote_step_view', { step_number: stepNumber, step_name: stepNameFor(stepNumber) });
    };

    const trackStepComplete = (stepNumber) => {
      if (completedSteps.has(stepNumber)) return;
      completedSteps.add(stepNumber);
      emitTracking('quote_step_complete', { step_number: stepNumber, step_name: stepNameFor(stepNumber) });
    };

    const controlsFor = (step) => Array.from(step.querySelectorAll('input, select, textarea'))
      .filter((control) => !control.disabled && !control.closest('[data-upload-app-slot]'));

    const focusFirstInvalid = (step) => {
      const controls = controlsFor(step);
      controls.forEach((control) => control.removeAttribute('aria-invalid'));
      const invalid = controls.find((control) => !control.checkValidity());
      if (!invalid) return null;
      invalid.setAttribute('aria-invalid', 'true');
      invalid.focus({ preventScroll: true });
      return invalid;
    };

    const validateStep = (stepNumber) => {
      const step = stepFor(stepNumber);
      if (!step) return true;
      if (focusFirstInvalid(step)) {
        setError(copy('complete_highlighted_field'));
        return false;
      }
      setError();
      return true;
    };

    const pageScrollQuery = window.matchMedia('(min-width: 990px), (hover: hover) and (pointer: fine)');
    const isHomepageMode = root.dataset.homepageMode === 'true';

    const pageScrollContainer = () => {
      if (pageScrollQuery.matches) {
        return document.querySelector('.page-wrapper') || document.scrollingElement || document.documentElement;
      }
      return document.scrollingElement || document.documentElement;
    };

    let releaseStepViewport = null;

    const actionRailFor = (step) => step?.querySelector('.tan-quick__actions, .tan-quick__final-actions');

    const captureStepViewport = (actionRail = null) => {
      const container = pageScrollContainer();
      if (!(container instanceof HTMLElement)) return null;
      return {
        container,
        left: container.scrollLeft,
        top: container.scrollTop,
        rootHeight: Math.ceil(root.getBoundingClientRect().height),
        actionBottom: actionRail instanceof HTMLElement ? actionRail.getBoundingClientRect().bottom : null,
      };
    };

    const restoreStepViewport = (viewport, activeStep = null) => {
      if (!viewport || !(viewport.container instanceof HTMLElement)) return;
      const maximumTop = Math.max(0, viewport.container.scrollHeight - viewport.container.clientHeight);
      const maximumLeft = Math.max(0, viewport.container.scrollWidth - viewport.container.clientWidth);
      if (viewport.alignToStepStart) {
        const isDocumentScroller = viewport.container === document.scrollingElement
          || viewport.container === document.documentElement
          || viewport.container === document.body;
        const containerTop = isDocumentScroller ? 0 : viewport.container.getBoundingClientRect().top;
        const rootTop = root.getBoundingClientRect().top;
        const stepStartTop = viewport.container.scrollTop + rootTop - containerTop;
        viewport.container.scrollTop = Math.max(0, Math.min(maximumTop, stepStartTop));
        viewport.container.scrollLeft = Math.max(0, Math.min(maximumLeft, viewport.left));
        return;
      }
      viewport.container.scrollTop = Math.max(0, Math.min(maximumTop, viewport.top));
      viewport.container.scrollLeft = Math.max(0, Math.min(maximumLeft, viewport.left));
      if (!Number.isFinite(viewport.actionBottom)) return;
      const actionRail = actionRailFor(activeStep);
      if (!(actionRail instanceof HTMLElement)) return;
      const actionDelta = actionRail.getBoundingClientRect().bottom - viewport.actionBottom;
      if (Math.abs(actionDelta) <= 0.5) return;
      const anchoredTop = viewport.container.scrollTop + actionDelta;
      viewport.container.scrollTop = Math.max(0, Math.min(maximumTop, anchoredTop));
    };

    const showStep = (stepNumber, options = {}) => {
      releaseStepViewport?.();
      const viewport = options.viewport;
      let viewportFrame = 0;
      let previousOverflowAnchor = '';
      let previousScrollBehavior = '';
      let previousMinHeight = '';
      let activeStep = null;
      if (viewport?.container instanceof HTMLElement) {
        stepTransitioning = true;
        previousOverflowAnchor = viewport.container.style.overflowAnchor;
        previousScrollBehavior = viewport.container.style.scrollBehavior;
        previousMinHeight = root.style.minHeight;
        viewport.container.style.overflowAnchor = 'none';
        viewport.container.style.scrollBehavior = 'auto';
        root.style.minHeight = `${viewport.rootHeight}px`;
        let released = false;
        releaseStepViewport = () => {
          if (released) return;
          released = true;
          if (viewportFrame && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(viewportFrame);
          root.style.minHeight = previousMinHeight;
          viewport.container.style.overflowAnchor = previousOverflowAnchor;
          viewport.container.style.scrollBehavior = previousScrollBehavior;
          restoreStepViewport(viewport, activeStep);
          stepTransitioning = false;
          releaseStepViewport = null;
        };
      }

      currentStep = Math.max(1, Math.min(steps.length, Number(stepNumber) || 1));
      steps.forEach((step) => {
        const active = Number(step.dataset.quickStep) === currentStep;
        step.hidden = !active;
        step.setAttribute('aria-hidden', String(!active));
        if (active) activeStep = step;
      });
      root.dataset.currentStep = String(currentStep);
      setError();
      trackStepView(currentStep);
      if (currentStep === steps.length) updateReview();
      restoreStepViewport(viewport, activeStep);
      if (viewport && typeof window.requestAnimationFrame === 'function') {
        viewportFrame = window.requestAnimationFrame(() => {
          viewportFrame = 0;
          root.style.minHeight = previousMinHeight;
          void root.offsetHeight;
          restoreStepViewport(viewport, activeStep);
          if (Number.isFinite(viewport.actionBottom)) {
            viewportFrame = window.requestAnimationFrame(() => {
              viewportFrame = 0;
              restoreStepViewport(viewport, activeStep);
              releaseStepViewport?.();
            });
          } else {
            releaseStepViewport?.();
          }
        });
      } else {
        releaseStepViewport?.();
      }
    };

    const roundBudget = (amount, increment) => {
      const safeIncrement = Math.max(1, Number(increment) || 1);
      return Math.round((Number(amount) || 0) / safeIncrement) * safeIncrement;
    };

    const convertedBudgetFor = (baseValue) => {
      const details = currencyDetailsFor(selectedCurrency) || { rate: 1, roundTo: 10 };
      return roundBudget((Number(baseValue) || 0) * Number(details.rate || 1), details.roundTo);
    };

    const formatNumber = (value) => {
      try {
        return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Number(value) || 0);
      } catch (_) {
        return String(Math.round(Number(value) || 0));
      }
    };

    const formatBudget = (baseValue, trailingPlus = false) => {
      const normalizedValue = convertedBudgetFor(baseValue);
      const prefixSymbols = {
        EUR: '€', USD: '$', CAD: 'CA$', GBP: '£', AUD: 'A$', NZD: 'NZ$', JPY: '¥', CNY: 'CN¥',
      };
      const number = formatNumber(normalizedValue);
      const formatted = prefixSymbols[selectedCurrency]
        ? `${prefixSymbols[selectedCurrency]}${number}`
        : `${number} ${selectedCurrency}`;
      return trailingPlus ? `${formatted}+` : formatted;
    };

    const currencyReference = () => {
      const details = currencyDetailsFor(selectedCurrency) || { rate: 1, roundTo: 10 };
      if (selectedCurrency === baseCurrency) {
        return `Base currency ${baseCurrency}; rounded to nearest ${details.roundTo} ${selectedCurrency}`;
      }
      return `1 ${baseCurrency} = ${details.rate} ${selectedCurrency}; ${cleanText(currencyConfig.source, 'Indicative reference rate')} (${cleanText(currencyConfig.asOf, 'date not supplied')}); rounded to nearest ${details.roundTo} ${selectedCurrency}`;
    };

    const updateCurrencyFields = (baseValue, noBudget = false, trailingPlus = false) => {
      const localValue = convertedBudgetFor(baseValue);
      if (budgetCurrencyField) budgetCurrencyField.value = selectedCurrency;
      if (budgetBaseField) budgetBaseField.value = noBudget
        ? 'No fixed budget / not sure'
        : `${baseValue}${trailingPlus ? '+' : ''} ${baseCurrency}`;
      if (budgetRateField) budgetRateField.value = currencyReference();
      if (budgetField) budgetField.value = noBudget
        ? 'No fixed budget / not sure'
        : `Approx. ${localValue}${trailingPlus ? '+' : ''} ${selectedCurrency}`;
      if (budgetRange) budgetRange.setAttribute('aria-label', copy('working_budget_in', { currency: selectedCurrency }));
      root.dataset.selectedCurrency = selectedCurrency;
    };

    const budgetLevelFor = (value) => budgetCards.find((card) => {
      const minimum = Number(card.dataset.budgetMin) || 0;
      const maximum = Number(card.dataset.budgetMax) || Number.POSITIVE_INFINITY;
      return value >= minimum && value <= maximum;
    }) || budgetCards.reduce((closest, card) => {
      if (!closest) return card;
      const target = Number(card.dataset.budgetValue) || 0;
      const closestTarget = Number(closest.dataset.budgetValue) || 0;
      return Math.abs(value - target) < Math.abs(value - closestTarget) ? card : closest;
    }, null);

    const showBudgetLevel = (activeCard, noBudget = false, preview = false) => {
      const activeLevel = activeCard ? Number(activeCard.dataset.budgetLevel) : -1;
      budgetCards.forEach((card) => {
        const active = !noBudget && card === activeCard;
        card.classList.toggle('is-active', active);
        card.setAttribute('aria-pressed', String(active));
      });
      budgetDetails.forEach((detail) => {
        const active = !noBudget && Number(detail.dataset.budgetDetail) === activeLevel;
        detail.hidden = !active;
        detail.querySelectorAll('[data-budget-ack]').forEach((input) => { input.disabled = !active; });
      });
      if (budgetUnsure) budgetUnsure.hidden = !noBudget;
      budgetShowcase?.classList.toggle('is-disabled', noBudget);
      if (!preview && budgetTierField) {
        budgetTierField.value = noBudget
          ? 'unsure'
          : activeLevel >= 0 ? `level_${activeLevel}` : 'not_selected';
      }
      if (!preview) root.dataset.budgetLevel = noBudget ? 'unsure' : String(activeLevel);
    };

    const updateBudgetTicks = (value, noBudget = false) => {
      let closestTick = null;
      budgetTicks.forEach((tick) => {
        const tickValue = Number(tick.dataset.budgetTick) || 0;
        if (!closestTick || Math.abs(value - tickValue) < Math.abs(value - Number(closestTick.dataset.budgetTick))) closestTick = tick;
      });
      budgetTicks.forEach((tick) => tick.classList.toggle('is-active', !noBudget && tick === closestTick));
    };

    const refreshBudgetTickLabels = () => {
      budgetTicks.forEach((tick) => {
        const tickValue = Number(tick.dataset.budgetTick) || 0;
        const label = tick.querySelector('[data-budget-tick-label]');
        if (label && budgetRange) label.textContent = formatBudget(tickValue, tickValue >= Number(budgetRange.max));
      });
    };

    const updateBudget = () => {
      if (!budgetRange || !budgetOutput || !budgetField) return;
      const noBudget = Boolean(budgetUnset?.checked);
      const value = Number(budgetRange.value) || 0;
      const min = Number(budgetRange.min) || 0;
      const max = Number(budgetRange.max) || Math.max(value, 1);
      budgetRange.disabled = noBudget;
      budgetRange.closest('.tan-quick__range-wrap')?.classList.toggle('is-disabled', noBudget);
      updateBudgetTicks(value, noBudget);

      if (noBudget) {
        budgetOutput.textContent = copy('budget_not_sure');
        updateCurrencyFields(value, true);
        showBudgetLevel(null, true);
        root.dataset.budgetBand = 'unknown';
        return;
      }

      const percentage = Math.max(0, Math.min(100, ((value - min) / Math.max(1, max - min)) * 100));
      budgetRange.style.setProperty('--range-progress', `${percentage}%`);
      budgetOutput.style.setProperty('--range-progress', `${percentage}%`);
      budgetOutput.textContent = `≈ ${formatBudget(value, value >= max)}`;
      updateCurrencyFields(value, false, value >= max);
      showBudgetLevel(budgetLevelFor(value));
      root.dataset.budgetBand = currentBudgetBand();
    };

    const updatePurpose = () => {
      const selected = purposeChoices.find((choice) => choice.checked);
      const selectedPurpose = selected?.dataset.purposeChoice || '';
      purposePanels.forEach((panel) => {
        const active = panel.dataset.purposePanel === selectedPurpose;
        panel.hidden = !active;
        panel.querySelectorAll('input, select, textarea').forEach((control) => { control.disabled = !active; });
      });
      root.dataset.signPurpose = selectedPurpose;
    };

    const selectedDeliveryCountryCode = () => {
      const selectedOption = deliveryCountry?.selectedOptions?.[0];
      const explicitCode = cleanText(selectedOption?.dataset.countryCode).toUpperCase();
      if (explicitCode) return explicitCode;
      const codeFromValue = cleanText(selectedOption?.value).match(/\(([A-Z]{2})\)\s*$/i)?.[1];
      return cleanText(codeFromValue, marketCountry).toUpperCase();
    };

    const updateMarketExamples = () => {
      const deliveryExample = marketExamples[selectedDeliveryCountryCode()];
      if (deliveryCity) {
        deliveryCity.placeholder = deliveryExample?.city
          ? copy('city_example', { city: deliveryExample.city })
          : copy('city_example_generic');
      }
    };

    const updatePostcodeState = () => {
      const unsure = Boolean(postcodeUnsure?.checked);
      if (!deliveryPostcode) return;
      deliveryPostcode.disabled = unsure;
      deliveryPostcode.required = !unsure;
      deliveryPostcode.closest('.tan-quick__field')?.classList.toggle('is-disabled', unsure);
    };

    const selectedControlFor = (name) => {
      const controls = Array.from(form.elements).filter((control) => control.name === name && !control.disabled);
      return controls.find((control) => {
        const type = cleanText(control.type).toLowerCase();
        return ['radio', 'checkbox'].includes(type) ? control.checked : true;
      });
    };

    const formValue = (name) => {
      const selected = selectedControlFor(name);
      if (!selected) return '';
      return cleanText(selected.value);
    };

    const formDisplayValue = (name) => {
      const selected = selectedControlFor(name);
      if (!selected) return '';
      if (selected.tagName === 'SELECT') {
        const value = cleanText(selected.value);
        return value ? cleanText(selected.selectedOptions?.[0]?.textContent, value) : '';
      }
      const type = cleanText(selected.type).toLowerCase();
      if (['radio', 'checkbox'].includes(type)) {
        const label = selected.closest('label');
        const visibleLabel = cleanText(
          selected.dataset.displayValue
          || label?.querySelector('[data-form-display-value]')?.textContent
          || label?.querySelector('strong')?.textContent
          || label?.querySelector('span')?.textContent
          || label?.textContent,
        );
        if (visibleLabel) return visibleLabel;
      }
      return cleanText(selected.value);
    };

    const pruneBlankContactFields = (formData) => {
      if (!formData) return;
      const contactNames = new Set();
      formData.forEach((_, name) => {
        if (/^contact\[.+\]$/.test(name)) contactNames.add(name);
      });
      contactNames.forEach((name) => {
        const values = formData.getAll(name);
        const populatedValues = values.filter((value) => {
          if (typeof value === 'string') return Boolean(cleanText(value));
          if (typeof File !== 'undefined' && value instanceof File) return Boolean(cleanText(value.name));
          return true;
        });
        if (populatedValues.length === values.length) return;
        formData.delete(name);
        populatedValues.forEach((value) => formData.append(name, value));
      });
    };

    const savePendingSubmission = () => {
      const fileCount = actualUploadCount();
      const category = ['personal', 'event', 'business'].includes(cleanText(root.dataset.signPurpose))
        ? cleanText(root.dataset.signPurpose)
        : 'general';
      const typeByCategory = {
        personal: formValue('contact[Personal use]'),
        event: formValue('contact[Event type]'),
        business: formValue('contact[Type of business]'),
      };
      const record = {
        version: 1,
        createdAt: Date.now(),
        pathname: window.location.pathname,
        category,
        type: quoteSlug(typeByCategory[category]),
        placement: category === 'business' ? quoteSlug(formValue('contact[Business display location]'), '') : '',
        marketCountry,
        quoteCurrency: selectedCurrency,
        submissionId: typeof window.crypto?.randomUUID === 'function'
          ? window.crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        budgetTier: currentBudgetTierCode(),
        budgetBand: currentBudgetBand(),
        hasFile: fileCount > 0,
        fileCount,
        restoredDraft: root.dataset.draftRestored === 'true',
      };
      try { window.sessionStorage.setItem(submissionStorageKey(root), JSON.stringify(record)); } catch (_) { /* Submission must continue if storage is unavailable. */ }
    };

    const setReviewValue = (element, value, fallback) => {
      if (!element) return;
      const normalized = cleanText(value);
      element.textContent = normalized || fallback;
      element.dataset.empty = String(!normalized);
    };

    const setReviewFacts = (element, rows, fallback) => {
      if (!element) return;
      const factRows = rows
        .map((row) => row
          .map(([label, value]) => [cleanText(label), cleanText(value)])
          .filter(([, value]) => Boolean(value)))
        .filter((row) => row.length);
      element.replaceChildren();
      if (!factRows.length) {
        element.textContent = fallback;
        element.dataset.empty = 'true';
        return;
      }

      const list = document.createElement('span');
      list.className = 'tan-quick__review-facts';
      factRows.forEach((facts) => {
        const row = document.createElement('span');
        row.className = 'tan-quick__review-fact-line';
        facts.forEach(([label, value]) => {
          const fact = document.createElement('span');
          const labelElement = document.createElement('span');
          const valueElement = document.createElement('strong');
          fact.className = 'tan-quick__review-fact';
          labelElement.textContent = `${label}:`;
          valueElement.textContent = value;
          fact.append(labelElement, valueElement);
          row.append(fact);
        });
        list.append(row);
      });
      element.append(list);
      element.dataset.empty = 'false';
    };

    const uploadFileExtensions = /\.(?:ai|eps|pdf|svg|png|jpe?g|tiff?)$/i;
    const uploadRecords = {
      primary: new Map(),
      secondary: new Map(),
    };
    const pendingUploads = {
      primary: 0,
      secondary: 0,
    };

    const uploadUrlFromDetail = (detail) => {
      const value = cleanText(detail?.url || detail?.file?.url || detail?.meta?.url);
      return /^https:\/\//i.test(value) ? value : '';
    };

    const syncUploadedFileLinks = () => {
      if (!uploadedFileLinksField) return;
      const links = [];
      Object.values(uploadRecords).forEach((records) => records.forEach((record) => {
        if (record.url && !links.includes(record.url)) links.push(record.url);
      }));
      uploadedFileLinksField.value = links.join('\n');
    };

    const pendingUploadCount = () => Object.values(pendingUploads)
      .reduce((total, count) => total + Math.max(0, count), 0);

    const uploadFileNameFromDetail = (detail) => {
      const candidates = [
        detail?.name,
        detail?.filename,
        detail?.fileName,
        detail?.originalName,
        detail?.original_name,
        detail?.meta?.name,
        detail?.meta?.filename,
        detail?.meta?.fileName,
        detail?.meta?.originalName,
        detail?.meta?.original_name,
        detail?.file?.name,
        detail?.url,
      ];
      for (const candidate of candidates) {
        if (!candidate) continue;
        let decoded = String(candidate);
        try { decoded = decodeURIComponent(decoded); } catch (_) { /* Keep the original value. */ }
        const withoutQuery = decoded.split(/[?#]/)[0];
        const fileName = cleanText(withoutQuery.split(/[\\/]/).pop());
        if (uploadFileExtensions.test(fileName)) return fileName;
      }
      return '';
    };

    const uploadRecordKey = (detail, fileName = '') => cleanText(
      detail?.id || detail?.uuid || detail?.key || detail?.url || fileName,
    );

    const uploadFileNamesFor = (slotName) => {
      const slot = root.querySelector(`[data-upload-app-slot="${slotName}"]`);
      if (!slot) return { names: [], count: 0, externalWidget: false };
      const names = [];
      const remember = (fileName) => {
        const normalized = cleanText(fileName);
        if (normalized && !names.some((name) => name.toLowerCase() === normalized.toLowerCase())) names.push(normalized);
      };

      const records = uploadRecords[slotName] || new Map();
      records.forEach((record) => {
        if (record.name) remember(record.name);
      });

      const placeholder = slot.querySelector('[data-upload-lift-placeholder]');
      const externalWidget = Array.from(slot.children).some((child) => child !== placeholder);
      const anonymousCount = Array.from(records.values()).filter((record) => !record.name).length;
      return { names: names.slice(0, 4), count: names.length + anonymousCount, externalWidget };
    };

    const actualUploadCount = () => Math.max(0, Math.min(8, Object.values(uploadRecords)
      .reduce((total, records) => total + records.size, 0)));

    const trackConfirmedUpload = (slotName) => {
      const fileCount = actualUploadCount();
      if (!fileCount || fileCount <= lastTrackedUploadCount) return;
      lastTrackedUploadCount = fileCount;
      trackQuoteStartOnce();
      emitTracking('quote_upload_added', {
        upload_slot: ['primary', 'secondary'].includes(slotName) ? slotName : 'other',
        has_file: 1,
        file_count: fileCount,
      });
    };

    const formatUploadReview = (summary, emptyText, widgetText) => {
      if (summary.names.length) {
        const additionalCount = Math.max(0, summary.count - summary.names.length);
        const additionalLabel = additionalCount ? copy('review_upload_more', { count: additionalCount }) : '';
        return [summary.names.join(', '), additionalLabel].filter(Boolean).join(' ');
      }
      if (summary.count) {
        let category = 'other';
        try {
          category = new Intl.PluralRules(locale).select(summary.count);
        } catch (_) {
          category = summary.count === 1 ? 'one' : 'other';
        }
        if (!['zero', 'one', 'two', 'few', 'many', 'other'].includes(category)) category = 'other';
        return copy(`review_uploaded_file_${category}`, { count: summary.count })
          || copy('review_uploaded_file_other', { count: summary.count });
      }
      return summary.externalWidget ? widgetText : emptyText;
    };

    const formatReviewDate = (value) => {
      const match = cleanText(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!match) return cleanText(value);
      try {
        const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
        return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(date);
      } catch (_) {
        return cleanText(value);
      }
    };

    const summarizeReviewText = (value, maximumLength = 220) => {
      const normalized = cleanText(value);
      if (normalized.length <= maximumLength) return normalized;
      return `${normalized.slice(0, maximumLength).trimEnd()}…`;
    };

    const updateEmailSuggestion = (email) => {
      if (!emailSuggestion || !emailCorrectionButton) return;
      const separatorIndex = email.lastIndexOf('@');
      const localPart = separatorIndex > 0 ? email.slice(0, separatorIndex) : '';
      const domain = separatorIndex > 0 ? email.slice(separatorIndex + 1).toLowerCase() : '';
      const correctedDomain = emailDomainCorrections[domain];
      const correctedEmail = localPart && correctedDomain ? `${localPart}@${correctedDomain}` : '';
      emailSuggestion.hidden = !correctedEmail;
      emailCorrectionButton.textContent = correctedEmail;
      emailCorrectionButton.dataset.correctedEmail = correctedEmail;
      if (correctedEmail) emailCorrectionButton.setAttribute('aria-label', copy('corrected_email_aria', { email: correctedEmail }));
      else emailCorrectionButton.removeAttribute('aria-label');
    };

    const updateReview = () => {
      const name = cleanText(contactNameInput?.value);
      const email = cleanText(emailInput?.value);
      setReviewValue(reviewName, name, copy('review_name_empty'));
      setReviewValue(reviewEmail, email, copy('review_email_empty'));
      updateEmailSuggestion(email);

      const primaryUpload = uploadFileNamesFor('primary');
      const secondaryUpload = uploadFileNamesFor('secondary');
      const hasArtworkShareLink = Boolean(formValue('contact[Artwork share link]'));
      if (reviewPrimaryFilesSection) reviewPrimaryFilesSection.hidden = primaryUpload.count === 0 && !hasArtworkShareLink;
      if (reviewSecondaryFilesSection) reviewSecondaryFilesSection.hidden = secondaryUpload.count === 0;
      if (reviewEmptyFilesSection) reviewEmptyFilesSection.hidden = primaryUpload.count + secondaryUpload.count > 0 || hasArtworkShareLink;
      if (primaryUpload.count) {
        setReviewValue(
          reviewPrimaryFiles,
          formatUploadReview(primaryUpload, copy('review_artwork_added'), copy('review_artwork_added')),
          copy('review_artwork_added'),
        );
      } else if (hasArtworkShareLink) {
        setReviewValue(reviewPrimaryFiles, copy('review_viewable_share_link_added'), copy('review_share_link_added'));
      }
      if (secondaryUpload.count) {
        setReviewValue(
          reviewSecondaryFiles,
          formatUploadReview(secondaryUpload, copy('review_references_added'), copy('review_references_added')),
          copy('review_references_added'),
        );
      }

      const selectedPurposeChoice = purposeChoices.find((choice) => choice.checked);
      const purposeCode = cleanText(selectedPurposeChoice?.dataset.purposeChoice).toLowerCase();
      const purposeContactValue = formValue('contact[Sign purpose]');
      const purpose = formDisplayValue('contact[Sign purpose]');
      const emailProjectDetails = [purposeContactValue];
      const projectDetails = [[[copy('review_project_type'), purpose]]];
      if (purposeCode === 'personal') {
        const personalUseContactValue = formValue('contact[Personal use]');
        const personalUse = formDisplayValue('contact[Personal use]');
        if (personalUseContactValue) emailProjectDetails.push(personalUseContactValue);
        if (personalUse) projectDetails[0].push([copy('review_space_use'), personalUse]);
      } else if (purposeCode === 'event') {
        const eventTypeContactValue = formValue('contact[Event type]');
        const eventType = formDisplayValue('contact[Event type]');
        const eventDateContactValue = formValue('contact[Event date]');
        const eventDate = formatReviewDate(eventDateContactValue);
        const eventDetails = [];
        if (eventTypeContactValue) emailProjectDetails.push(eventTypeContactValue);
        if (eventDateContactValue) emailProjectDetails.push(eventDateContactValue);
        if (eventType) eventDetails.push([copy('review_event_type'), eventType]);
        if (eventDate) eventDetails.push([copy('review_event_date'), eventDate]);
        if (eventDetails.length) projectDetails.push(eventDetails);
      } else if (purposeCode === 'business') {
        const brand = formValue('contact[Business or brand name]');
        const businessTypeContactValue = formValue('contact[Type of business]');
        const displayLocationContactValue = formValue('contact[Business display location]');
        const businessType = formDisplayValue('contact[Type of business]');
        const displayLocation = formDisplayValue('contact[Business display location]');
        if (brand) emailProjectDetails.push(brand);
        if (businessTypeContactValue) emailProjectDetails.push(businessTypeContactValue);
        if (displayLocationContactValue) emailProjectDetails.push(displayLocationContactValue);
        if (brand) projectDetails[0].push([copy('review_brand'), brand]);
        const businessDetails = [];
        if (businessType) businessDetails.push([copy('review_business_type'), businessType]);
        if (displayLocation) businessDetails.push([copy('review_display'), displayLocation]);
        if (businessDetails.length) projectDetails.push(businessDetails);
      }
      setReviewFacts(reviewProject, projectDetails, copy('review_project_empty'));

      setReviewFacts(reviewSetup, [[
        [copy('review_environment'), formDisplayValue('contact[Installation environment]')],
        [copy('review_backboard'), formDisplayValue('contact[Backboard style]')],
      ]], copy('review_setup_empty'));

      const width = formValue('contact[Approximate width]');
      const sizeUnitContactValue = formValue('contact[Size unit]');
      const sizeUnit = formDisplayValue('contact[Size unit]');
      const quantity = formValue('contact[Quantity]');
      const deliveryTimeContactValue = formValue('contact[Preferred delivery time]');
      const countryContactValue = formValue('contact[Delivery country]');
      const deliveryTime = formDisplayValue('contact[Preferred delivery time]');
      const country = formDisplayValue('contact[Delivery country]');
      const city = formValue('contact[Delivery city]');
      const postcode = postcodeUnsure?.checked ? '' : formValue('contact[Delivery postal code]');
      const size = width ? `${width} ${sizeUnit || 'cm'}` : '';
      const locality = [city, postcode].filter(Boolean).join(' ');
      const destination = [locality, country].filter(Boolean).join(', ');
      const sizeDeliveryDetails = [
        [[copy('review_width'), size], [copy('review_quantity'), quantity]],
        [[copy('review_timing'), deliveryTime || copy('review_not_specified')], [copy('review_destination'), destination]],
      ];
      if (postcodeUnsure?.checked) sizeDeliveryDetails.push([[copy('review_postal_code'), copy('review_not_known_yet')]]);
      setReviewFacts(reviewSizeDelivery, sizeDeliveryDetails, copy('review_size_delivery_empty'));

      const maximumBudget = Number(budgetRange?.max) || Number.POSITIVE_INFINITY;
      const selectedBudget = copy('review_approx_budget', {
        amount: formatNumber(convertedBudgetFor(Number(budgetRange?.value))),
        plus: Number(budgetRange?.value) >= maximumBudget ? '+' : '',
        currency: selectedCurrency,
      });
      const activeBudgetLabel = cleanText(
        budgetCards.find((card) => card.classList.contains('is-active'))
          ?.querySelector('.tan-quick__budget-card-label')?.textContent,
      );
      const budgetDetails = [[
        [copy('review_budget'), budgetUnset?.checked ? copy('review_no_fixed_budget') : selectedBudget],
        [copy('review_level'), budgetUnset?.checked ? copy('review_designer_recommendation') : activeBudgetLabel],
      ]];
      if (formValue('contact[Low-budget acknowledgement]')) {
        budgetDetails.push([[copy('review_budget_scope_notice'), copy('review_confirmed')]]);
      }
      setReviewFacts(reviewBudget, budgetDetails, copy('review_budget_empty'));

      const notes = summarizeReviewText(formValue('contact[Logo and design requirements]'));
      if (reviewNotesSection) reviewNotesSection.hidden = !notes;
      if (notes) setReviewValue(reviewNotes, notes, '');

      if (quoteEmailSummaryField) {
        const customer = [name, email].filter(Boolean).join(' — ') || 'Not entered';
        const project = emailProjectDetails.filter(Boolean).join(' — ') || 'Not selected';
        const setup = [
          formValue('contact[Installation environment]'),
          formValue('contact[Backboard style]'),
        ].filter(Boolean).join(' — ') || 'Not selected';
        const budget = [
          budgetUnset?.checked ? 'No fixed budget / designer recommendation requested' : cleanText(budgetField?.value),
          cleanText(budgetTierField?.value),
        ].filter(Boolean).join(' — ') || 'Not selected';
        const emailSize = width ? `${width} ${sizeUnitContactValue || 'cm'}` : '';
        const sizeAndQuantity = [
          emailSize || 'Size not entered',
          `Quantity ${quantity || 'not entered'}`,
        ].join(' — ');
        const emailDestination = [locality, countryContactValue].filter(Boolean).join(', ');
        const delivery = [deliveryTimeContactValue, emailDestination].filter(Boolean).join(' — ') || 'Not entered';
        const fileParts = [];
        if (primaryUpload.count) fileParts.push(`${primaryUpload.count} initial ${primaryUpload.count === 1 ? 'file' : 'files'}`);
        if (secondaryUpload.count) fileParts.push(`${secondaryUpload.count} additional ${secondaryUpload.count === 1 ? 'file' : 'files'}`);
        if (hasArtworkShareLink) fileParts.push('file-sharing link');
        quoteEmailSummaryField.value = [
          `Customer: ${customer}`,
          `Budget: ${budget}`,
          `Size / quantity: ${sizeAndQuantity}`,
          `Project: ${project}`,
          `Sign setup: ${setup}`,
          `Delivery: ${delivery}`,
          `Files: ${fileParts.join(', ') || 'None added'}`,
        ].join('\n');
      }
    };

    let uploadReviewFrame = 0;
    const scheduleUploadReview = () => {
      if (currentStep !== steps.length) return;
      if (uploadReviewFrame && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(uploadReviewFrame);
      if (typeof window.requestAnimationFrame !== 'function') {
        updateReview();
        return;
      }
      uploadReviewFrame = window.requestAnimationFrame(() => {
        uploadReviewFrame = 0;
        updateReview();
      });
    };

    root.querySelectorAll('[data-upload-app-slot]').forEach((slot) => {
      new MutationObserver(scheduleUploadReview).observe(slot, { childList: true, subtree: true, characterData: true });
      const slotName = cleanText(slot.dataset.uploadAppSlot);
      const records = uploadRecords[slotName];
      slot.addEventListener('change', (event) => {
        if (!records || !event.target?.matches?.('input[type="file"]')) return;
        const selectedCount = event.target.files?.length || 0;
        if (selectedCount > 0) pendingUploads[slotName] += selectedCount;
      });
      slot.addEventListener('upload:added', (event) => {
        const detail = event.detail || {};
        if (detail.meta?.variant === 'thumbnail' || !records) return;
        const name = uploadFileNameFromDetail(detail);
        const url = uploadUrlFromDetail(detail);
        if (!url) return;
        const key = uploadRecordKey(detail, name) || `upload-${++anonymousUploadSequence}`;
        records.set(key, { name, url });
        pendingUploads[slotName] = Math.max(0, pendingUploads[slotName] - 1);
        syncUploadedFileLinks();
        trackConfirmedUpload(slotName);
        scheduleUploadReview();
      });
      slot.addEventListener('upload:removed', (event) => {
        const detail = event.detail || {};
        if (detail.meta?.variant === 'thumbnail' || !records) return;
        const name = uploadFileNameFromDetail(detail);
        const key = uploadRecordKey(detail, name);
        let removedConfirmedUpload = false;
        if (key && records.has(key)) removedConfirmedUpload = records.delete(key);
        else if (name) {
          const match = Array.from(records.entries()).find(([, record]) => record.name === name);
          if (match) removedConfirmedUpload = records.delete(match[0]);
        }
        if (!removedConfirmedUpload) pendingUploads[slotName] = Math.max(0, pendingUploads[slotName] - 1);
        lastTrackedUploadCount = Math.min(lastTrackedUploadCount, actualUploadCount());
        syncUploadedFileLinks();
        scheduleUploadReview();
      });
    });

    form.addEventListener('reset', () => {
      Object.values(uploadRecords).forEach((records) => records.clear());
      Object.keys(pendingUploads).forEach((slotName) => { pendingUploads[slotName] = 0; });
      lastTrackedUploadCount = 0;
      syncUploadedFileLinks();
      scheduleUploadReview();
    });

    const draftControls = () => Array.from(form.elements).filter((control) => {
      const type = cleanText(control.type).toLowerCase();
      if (!control.name || control.disabled || control.matches?.('[data-no-draft]')) return false;
      if (control.closest?.('[data-upload-app-slot]')) return false;
      return !['hidden', 'file', 'submit', 'button', 'email', 'tel', 'password'].includes(type);
    });

    const saveDraft = (targetStep = currentStep) => {
      const scrollContainer = pageScrollContainer();
      const viewport = !isHomepageMode && scrollContainer instanceof HTMLElement
        ? { top: scrollContainer.scrollTop, left: scrollContainer.scrollLeft }
        : null;
      const values = {};
      draftControls().forEach((control) => {
        const type = cleanText(control.type).toLowerCase();
        if (type === 'radio') {
          if (control.checked) values[control.name] = control.value;
          return;
        }
        if (type === 'checkbox') {
          values[control.name] = control.checked ? (control.value || true) : false;
          return;
        }
        values[control.name] = control.value;
      });
      const draft = {
        version: 3,
        savedAt: Date.now(),
        step: Math.max(1, Math.min(steps.length, Number(targetStep) || 1)),
        values,
        ui: {
          budget: budgetRange?.value || '',
          budgetUnset: Boolean(budgetUnset?.checked),
          budgetSelected,
          quoteStarted,
          currency: selectedCurrency,
          viewport,
        },
      };
      try {
        window.localStorage.setItem(draftStorageKey, JSON.stringify(draft));
      } catch (_) {
        // The quote remains fully usable when local storage is unavailable.
      }
    };

    const scheduleDraftSave = (targetStep = currentStep) => {
      if (saveTimer) window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(() => saveDraft(targetStep), 220);
    };

    const restoreDraft = () => {
      let draft;
      try {
        const rawDraft = window.localStorage.getItem(draftStorageKey);
        if (!rawDraft) return null;
        draft = JSON.parse(rawDraft);
      } catch (_) {
        return null;
      }
      const maxAge = draftDays * 24 * 60 * 60 * 1000;
      if (!draft || draft.version !== 3 || !draft.savedAt || Date.now() - Number(draft.savedAt) > maxAge) {
        removeDraft();
        return null;
      }

      let restoredControls = 0;
      Object.entries(draft.values || {}).forEach(([name, value]) => {
        const controls = Array.from(form.elements).filter((control) => control.name === name && !control.matches?.('[data-no-draft]'));
        controls.forEach((control) => {
          const type = cleanText(control.type).toLowerCase();
          if (type === 'radio') control.checked = control.value === value;
          else if (type === 'checkbox') control.checked = value === true || value === control.value;
          else control.value = String(value ?? '');
          restoredControls += 1;
        });
      });

      if (budgetRange && draft.ui?.budget) budgetRange.value = String(draft.ui.budget);
      if (budgetUnset) budgetUnset.checked = Boolean(draft.ui?.budgetUnset);
      budgetSelected = true;
      quoteStarted = draft.ui?.quoteStarted === true;
      const draftCurrency = cleanText(draft.ui?.currency).toUpperCase();
      if (currencySelect && isSupportedCurrency(draftCurrency) && Array.from(currencySelect.options).some((option) => option.value === draftCurrency)) {
        selectedCurrency = draftCurrency;
        currencySelect.value = selectedCurrency;
      }
      if (!restoredControls && !draft.ui?.budget) return null;
      root.dataset.draftRestored = 'true';
      if (restoreBanner) restoreBanner.hidden = false;
      const restoredStep = Math.max(1, Math.min(steps.length, Number(draft.step) || 1));
      for (let stepNumber = 1; stepNumber < restoredStep; stepNumber += 1) completedSteps.add(stepNumber);
      const savedViewport = draft.ui?.viewport;
      const container = pageScrollContainer();
      const viewport = isHomepageMode
        ? captureStepViewport()
        : savedViewport && container instanceof HTMLElement
          && Number.isFinite(Number(savedViewport.top)) && Number.isFinite(Number(savedViewport.left))
          ? {
            container,
            top: Number(savedViewport.top),
            left: Number(savedViewport.left),
            rootHeight: Math.ceil(root.getBoundingClientRect().height),
            actionBottom: null,
          }
          : null;
      return { step: restoredStep, viewport };
    };

    root.querySelectorAll('[data-quick-next]').forEach((button) => button.addEventListener('click', () => {
      if (stepTransitioning) return;
      const completedStep = currentStep;
      if (!validateStep(completedStep)) return;
      trackStepComplete(completedStep);
      saveDraft(completedStep + 1);
      const alignToStepStart = completedStep >= 3 && completedStep < steps.length;
      const viewport = captureStepViewport();
      if (viewport) viewport.alignToStepStart = alignToStepStart;
      button.blur();
      showStep(completedStep + 1, { viewport });
    }));

    root.querySelectorAll('[data-quick-back]').forEach((button) => button.addEventListener('click', () => {
      if (stepTransitioning) return;
      saveDraft(currentStep - 1);
      const viewport = captureStepViewport(currentStep === 6 ? button.closest('.tan-quick__final-actions') : null);
      button.blur();
      showStep(currentStep - 1, { viewport });
    }));

    budgetRange?.addEventListener('input', () => {
      budgetSelected = true;
      updateBudget();
    });
    budgetUnset?.addEventListener('change', updateBudget);
    currencySelect?.addEventListener('change', () => {
      const requestedCurrency = cleanText(currencySelect.value).toUpperCase();
      if (!isSupportedCurrency(requestedCurrency)) return;
      selectedCurrency = requestedCurrency;
      try { window.sessionStorage.setItem(currencyStorageKey, selectedCurrency); } catch (_) { /* Selection still works. */ }
      refreshBudgetTickLabels();
      updateBudget();
    });
    purposeChoices.forEach((choice) => choice.addEventListener('change', updatePurpose));

    deliveryCountry?.addEventListener('change', updateMarketExamples);
    postcodeUnsure?.addEventListener('change', updatePostcodeState);

    budgetTicks.forEach((tick) => {
      const tickValue = Number(tick.dataset.budgetTick) || 0;
      tick.addEventListener('click', () => {
        if (!budgetRange) return;
        budgetSelected = true;
        if (budgetUnset) budgetUnset.checked = false;
        budgetRange.value = String(tickValue);
        updateBudget();
        scheduleDraftSave();
      });
    });

    budgetCards.forEach((card) => {
      card.addEventListener('click', () => {
        if (!budgetRange) return;
        budgetSelected = true;
        if (budgetUnset) budgetUnset.checked = false;
        budgetRange.value = String(Number(card.dataset.budgetValue) || budgetRange.value);
        updateBudget();
        scheduleDraftSave();
      });
      card.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse' && !budgetUnset?.checked) showBudgetLevel(card, false, true);
      });
      card.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') updateBudget();
      });
    });

    reviewEditButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (stepTransitioning) return;
        if (button.dataset.reviewEditTarget === 'final-files' && finalFilesSection) {
          button.blur();
          const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth';
          finalFilesSection.scrollIntoView({ behavior, block: 'center' });
          return;
        }
        const targetStep = Math.max(1, Math.min(steps.length - 1, Number(button.dataset.reviewEdit) || 1));
        saveDraft(targetStep);
        const viewport = captureStepViewport();
        if (viewport) viewport.alignToStepStart = true;
        button.blur();
        showStep(targetStep, { viewport });
      });
    });

    emailCorrectionButton?.addEventListener('click', () => {
      const correctedEmail = cleanText(emailCorrectionButton.dataset.correctedEmail);
      if (!correctedEmail || !emailInput) return;
      emailInput.value = correctedEmail;
      emailInput.dispatchEvent(new Event('input', { bubbles: true }));
      emailInput.focus({ preventScroll: true });
    });

    const trackQuoteInteraction = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const control = target?.closest('input, select, textarea, button, [data-upload-app-slot]');
      if (!control || !form.contains(control) || control === clearDraftButton || control.closest('[data-quote-clear-draft]')) return;
      trackQuoteStartOnce();
    };

    form.addEventListener('pointerdown', trackQuoteInteraction, { capture: true });
    form.addEventListener('keydown', trackQuoteInteraction, { capture: true });

    form.addEventListener('input', (event) => {
      trackQuoteStartOnce();
      updateReview();
      if (event.target?.matches?.('[data-no-draft]') || event.target?.closest?.('[data-upload-app-slot]')) return;
      scheduleDraftSave();
    });
    form.addEventListener('change', (event) => {
      trackQuoteStartOnce();
      updateReview();
      if (event.target?.matches?.('[data-no-draft]') || event.target?.closest?.('[data-upload-app-slot]')) return;
      scheduleDraftSave();
    });

    clearDraftButton?.addEventListener('click', () => {
      removeDraft();
      budgetSelected = true;
      form.reset();
      root.dataset.draftRestored = 'false';
      if (restoreBanner) restoreBanner.hidden = true;
      selectedCurrency = isSupportedCurrency(marketCurrency) ? marketCurrency : baseCurrency;
      if (currencySelect) currencySelect.value = selectedCurrency;
      updatePurpose();
      updateMarketExamples();
      updatePostcodeState();
      refreshBudgetTickLabels();
      updateBudget();
      setSubmittingState(false);
      updateReview();
      visitedSteps.clear();
      completedSteps.clear();
      emitTracking('quote_restart', { step_number: 1, step_name: stepNameFor(1) });
      root.dispatchEvent(new CustomEvent('tan:quote:reset'));
      showStep(1);
    });

    form.addEventListener('keydown', (event) => {
      const interactiveEnterTarget = ['TEXTAREA', 'BUTTON'].includes(event.target?.tagName);
      if (event.key === 'Enter' && !interactiveEnterTarget) event.preventDefault();
    });

    form.addEventListener('formdata', (event) => {
      if (quoteEmailSummaryField) event.formData.set('contact[Quote summary]', quoteEmailSummaryField.value);
      pruneBlankContactFields(event.formData);
    });

    form.addEventListener('submit', (event) => {
      if (pendingUploadCount() > 0) {
        event.preventDefault();
        setError(copy('wait_for_uploads'));
        errorBox?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      syncUploadedFileLinks();
      updateReview();
      for (let stepNumber = 1; stepNumber <= steps.length; stepNumber += 1) {
        if (!validateStep(stepNumber)) {
          event.preventDefault();
          showStep(stepNumber);
          return;
        }
      }
      trackStepComplete(steps.length);
      savePendingSubmission();
      window.setTimeout(() => {
        if (event.defaultPrevented) {
          setSubmittingState(false);
          return;
        }
        setSubmittingState(true);
      }, 0);
    });

    const restoredDraft = restoreDraft();
    updatePurpose();
    updateMarketExamples();
    updatePostcodeState();
    refreshBudgetTickLabels();
    updateBudget();
    window.addEventListener('pagehide', () => saveDraft(currentStep));
    window.addEventListener('pageshow', () => setSubmittingState(false));
    updateReview();
    if (restoredDraft) emitTracking('quote_restore', { step_number: restoredDraft.step, step_name: stepNameFor(restoredDraft.step) });
    showStep(restoredDraft?.step || 1, { viewport: restoredDraft?.viewport });
    root.dispatchEvent(new CustomEvent('tan:quote:ready', { bubbles: true }));
  }

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-tan-quick-quote]')) init(scope);
    scope.querySelectorAll?.('[data-tan-quick-quote]').forEach(init);
  };
  window.TANQuickQuoteRuntime = Object.assign(window.TANQuickQuoteRuntime || {}, {
    scan: initAll,
    buildSuccessUrl: (record, href) => successUrlFor(record, href).toString(),
    quoteSlug,
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
